import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EmptyState from '@/components/base/EmptyState';
import ErrorState from '@/components/base/ErrorState';
import Skeleton from '@/components/base/Skeleton';
import type { OrderView } from '@/types';

interface EscalationSectionProps {
  alerts: OrderView[] | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

export default function EscalationSection({ alerts, loading, error, onRetry }: EscalationSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="overflow-hidden rounded-lg border border-amber-200 bg-background-50">
      <div className="flex items-center gap-3 border-b border-amber-200 bg-amber-50/70 px-4 py-4 md:px-5">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-600">
          <i className="ri-user-follow-line text-xl leading-none" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-amber-900">{t('home.escalation.title')}</h2>
          <p className="mt-0.5 text-xs text-amber-800/80">{t('home.escalation.subtitle')}</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3 p-4 md:p-5">
          {[0, 1].map((i) => (
            <div key={i} className="rounded-lg border border-background-200 p-4">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="mt-2 h-4 w-48" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="p-4 md:p-5">
          <ErrorState
            compact
            title={t('home.escalationError')}
            description={t('common.errorText')}
            retryLabel={t('common.retry')}
            onRetry={onRetry}
          />
        </div>
      ) : !alerts || alerts.length === 0 ? (
        <div className="p-4 md:p-5">
          <EmptyState icon="ri-shield-check-line" title={t('home.escalation.empty')} />
        </div>
      ) : (
        <>
          <ul className="divide-y divide-background-100">
            {alerts.map((order) => (
              <li key={order.id} className="p-4 md:px-5 md:py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="lb-ltr-field text-sm font-semibold text-foreground-950">
                        {order.id}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
                        <i className="ri-error-warning-line text-[1.05em] leading-none" aria-hidden="true" />
                        {t('home.escalation.badge')}
                      </span>
                    </div>
                    <p className="mt-1.5 text-sm font-medium text-foreground-800">{order.customerName}</p>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-500">
                      {t('home.escalation.reason')}
                    </p>
                  </div>
                  <Link
                    to={`/dashboard/orders/${order.id}`}
                    className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary-500 px-3.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-eye-line text-base" aria-hidden="true" />
                    {t('home.escalation.viewOrder')}
                  </Link>
                </div>
              </li>
            ))}
          </ul>
          <div className="border-t border-background-100 bg-background-100/60 px-4 py-3 md:px-5">
            <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
              <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
              {t('home.escalation.note')}
            </p>
          </div>
        </>
      )}
    </div>
  );
}